/**
 * L'UNICA PORTA VERSO `localStorage` — con il `try` dentro e l'intestatario davanti.
 *
 * ## Perché esiste
 *
 * Prima di questo modulo il repo aveva **134 accessi diretti** allo storage, di cui **57 senza
 * `try`**. La distribuzione era al rovescio: i moduli periferici (`appLock`, `foodcost`, gli
 * storici) erano tutti protetti, e senza protezione erano i due punti da cui non si torna —
 * `AuthProvider`, che legge il refresh token nel `useState` iniziale, e `api/client.ts`, che legge
 * impronta, azienda e lingua **a ogni richiesta**.
 *
 * ⚠️ Un accesso a `localStorage` **solleva**, non torna `null`: basta che il browser abbia lo
 * storage negato (Safari con i cookie di terze parti bloccati dentro un iframe, un profilo con i
 * dati dei siti disattivati, una WebView irrigidita) e quella riga nel `useState` uccide l'app al
 * primo render — schermo bianco, prima di qualunque schermata d'errore. Nel frattempo
 * `isAuthenticatedSync()`, che il `try` ce l'ha, avrebbe risposto civilmente «non autenticato».
 *
 * ## Le due domande che una chiave si deve porre
 *
 * 1. **È del dispositivo o della persona?** Le chiavi non hanno mai avuto un intestatario, quindi
 *    su un browser condiviso — il tablet di reparto, il PC di sede — il secondo utente eredita le
 *    cose del primo: preferiti, preset di Reporting, modelli di comunicazione, segnalibri, sede
 *    scelta in magazzino. `ambito: 'persona'` mette l'id utente nella chiave e la separazione
 *    diventa una proprietà del nome, non una pulizia da ricordare al logout.
 * 2. **Sopravvive al logout?** Le chiavi di **sessione** vanno via (`pulisciSessione`), le altre
 *    restano: quelle della persona perché sono sue e le ritrova al rientro, quelle del dispositivo
 *    perché sono del dispositivo.
 *
 * ## Perché l'id utente sta qui e non si legge da `auth-state`
 *
 * `auth-state` usa queste primitive: se questo modulo importasse quello, il ciclo si chiuderebbe.
 * L'id arriva quindi per via imperativa da `AuthProvider` (`impostaUtente`), lo stesso schema — e
 * per la stessa ragione — del mirror sincrono usato dalle route guard.
 *
 * ⚠️ **Con l'utente ancora sconosciuto una chiave della persona ricade sul nome nudo.** Non è una
 * scorciatoia: fra il primo render e la risposta del refresh non c'è nessun id da mettere, e
 * rifiutare la lettura mostrerebbe «nessun preferito» a chi ne ha dieci. Al primo id conosciuto
 * `migraChiaviPersonali` porta quei valori sotto il nome intestato e cancella il nome nudo.
 */
import { PERSONAL_KEYS, STORAGE_KEYS } from './constants';

/** Chi sta guardando. `null` = nessuno. */
let currentUser: string | null = null;
/** `true` quando si è già provato a ripescarlo dal disco: la lettura si fa una volta sola. */
let hydrated = false;

/**
 * L'intestatario, ripescato al primo bisogno.
 *
 * ⚠️ **L'idratazione qui è la differenza fra una preferenza che si ritrova e una che sparisce a
 * ogni F5.** Su hard refresh la rotta monta prima che il refresh del token risponda (la guardia
 * dice sì grazie al refresh token persistito), e in quella finestra i componenti leggono già le
 * proprie chiavi in un inizializzatore di `useState`. Senza questa riga leggerebbero il nome nudo
 * — vuoto, dopo la migrazione — e la sede scelta in magazzino tornerebbe da capo ogni volta.
 *
 * È lo stesso schema, e per la stessa ragione, con cui i claim vengono persistiti per disegnare il
 * menu al primo render: `AuthProvider` la riscrive appena il token arriva.
 */
function user(): string | null {
  if (!hydrated) {
    hydrated = true;
    if (currentUser === null) {
      try {
        currentUser = localStorage.getItem(STORAGE_KEYS.userId);
      } catch {
        currentUser = null;
      }
    }
  }
  return currentUser;
}

/**
 * Le chiavi di **sessione**: al logout vanno via, o il prossimo che entra da questo browser si
 * porta dietro pezzi della sessione di prima.
 *
 * ⚠️ `portalAccessId` è in questo elenco e prima non lo era da nessuna parte: è il committente
 * selezionato di **quella** persona. Restando, la prima chiamata del portale del nuovo utente parte
 * con l'accesso di un altro — il server la respinge (`CLIENT_PORTAL_ACCESS_INVALID`) e il client la
 * dimentica, quindi si ripara da sé, ma è un 403 evitabile con una riga.
 */
const SESSION_KEYS: readonly string[] = [
  STORAGE_KEYS.refreshToken,
  STORAGE_KEYS.tenantId,
  STORAGE_KEYS.portalSession,
  STORAGE_KEYS.staffMembership,
  STORAGE_KEYS.claims,
  STORAGE_KEYS.portalAccessId,
  STORAGE_KEYS.userId,
];

/** `true` se questa chiave è intestata a una persona (elenco in `constants.ts`). */
function ofPerson(key: string): boolean {
  return (PERSONAL_KEYS as readonly string[]).includes(key);
}

/**
 * Il nome vero sotto cui il valore vive.
 *
 * Il separatore è `::` e non `:` perché i nomi già usano i due punti come gerarchia
 * (`maefwk10:warehouse:site`): un separatore che si confonde con la struttura rende impossibile
 * dire, guardando una chiave, dove finisce il nome e dove comincia l'intestatario.
 */
export function actualKey(chiave: string): string {
  const chi = user();
  if (!ofPerson(chiave) || !chi) return chiave;
  return `${chiave}::${chi}`;
}

/* ─────────────────────────────── le primitive ─────────────────────────────── */

export function leggiChiave(chiave: string): string | null {
  try {
    return localStorage.getItem(actualKey(chiave));
  } catch {
    return null;
  }
}

/**
 * Scrive, e non solleva **mai**.
 *
 * I due fallimenti veri sono la quota piena e lo storage negato, e in entrambi i casi la sessione
 * deve continuare: chi ha appena scelto una sede in magazzino non deve vedere una schermata rotta
 * perché una preferenza non si è potuta ricordare.
 */
export function writeKey(chiave: string, value: string): void {
  try {
    localStorage.setItem(actualKey(chiave), value);
  } catch { /* quota o storage negato: il gesto vale, la memoria no */ }
}

export function removeKey(chiave: string): void {
  try {
    localStorage.removeItem(actualKey(chiave));
  } catch { /* niente da togliere se non c'è niente da leggere */ }
}

/* ── sessionStorage: per-scheda, ed è la proprietà che serve a chi lo usa qui ── */

/**
 * `sessionStorage` non è «localStorage che scade»: è **per scheda**. È il posto giusto per
 * l'override cross-tenant (due schede su due aziende non si pestano) e per l'avviso da mostrare
 * dopo un ricaricamento. Nessuna di queste chiavi è intestata: la scheda è già lo scope.
 */
export function readSession(chiave: string): string | null {
  try {
    return sessionStorage.getItem(chiave);
  } catch {
    return null;
  }
}

export function scriviSessione(chiave: string, valore: string): void {
  try {
    sessionStorage.setItem(chiave, valore);
  } catch { /* come sopra */ }
}

export function removeSession(chiave: string): void {
  try {
    sessionStorage.removeItem(chiave);
  } catch { /* come sopra */ }
}

/* ─────────────────────── sessione, utente, migrazione ─────────────────────── */

/**
 * Dichiara chi sta guardando. Da chiamare **prima** di navigare, non in un effetto: chi decide
 * dove mandare l'utente appena entrato gira fuori da React e leggerebbe le chiavi di un altro.
 */
export function setUser(userId: string | null): void {
  currentUser = userId && userId.trim() !== '' ? userId : null;
  hydrated = true;
  /* Si persiste, o al prossimo hard refresh l'intestatario tornerebbe sconosciuto per il tempo del
     round-trip — che è esattamente la finestra in cui le pagine leggono le loro preferenze. */
  try {
    if (currentUser) localStorage.setItem(STORAGE_KEYS.userId, currentUser);
    else localStorage.removeItem(STORAGE_KEYS.userId);
  } catch { /* storage negato: resta l'id in memoria, che basta per questa sessione */ }
}

export function storageUser(): string | null {
  return user();
}

/**
 * Porta sotto il nome intestato i valori scritti quando l'utente non era ancora noto — cioè tutto
 * quello che i browser hanno accumulato **prima** che le chiavi avessero un intestatario.
 *
 * ⚠️ Il primo che entra dopo l'aggiornamento si prende i valori nudi, anche se li aveva scritti un
 * altro. È l'unica scelta che non butta niente, e non peggiora nulla: finora quei valori erano
 * **di tutti** — l'ultimo che scriveva vinceva. Dopo la migrazione la separazione è vera.
 *
 * Idempotente: se il nome intestato esiste già, il nudo viene solo rimosso (il valore intestato è
 * più recente per costruzione — l'ha scritto questa persona).
 */
export function migratePersonalKeys(): void {
  const chi = user();
  if (!chi) return;
  for (const chiave of PERSONAL_KEYS) {
    try {
      const bare = localStorage.getItem(chiave);
      if (bare === null) continue;
      const owned = `${chiave}::${chi}`;
      if (localStorage.getItem(owned) === null) localStorage.setItem(owned, bare);
      localStorage.removeItem(chiave);
    } catch { /* storage negato: non c'è niente da migrare */ }
  }
}

/**
 * Al logout: via le chiavi di sessione, e **solo** quelle.
 *
 * Non è un `localStorage.clear()` e la differenza non è di stile: nella stessa origine vivono la
 * coda delle timbrature non inviate — buttarla significa cancellare ore di lavoro di cui il server
 * non sa niente — e le preferenze delle persone che usano questo browser, che sono loro e le
 * ritrovano al rientro.
 */
export function clearSession(): void {
  for (const chiave of SESSION_KEYS) {
    try {
      localStorage.removeItem(chiave);
    } catch { /* niente da fare */ }
  }
  setUser(null);
}
